import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiArrowRightSLine, RiLoader4Line } from 'react-icons/ri';
import { usePermission } from '../../hooks/usePermission';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import DetailSection from './DetailSection';

/**
 * การ์ด "ข้อมูลที่เกี่ยวข้อง" ของหน้ารายละเอียด (เช่น โครงการ/สัญญาเช่า/ใบเสนอราคาของบริษัทนี้)
 *
 * - `permission` ไม่ผ่าน = ไม่ render และ **ไม่ยิง API** (ไม่มี 403 ขึ้นจอ · G-12 ลิงก์ข้ามโมดูลต้องมีสิทธิ์ปลายทาง)
 * - แสดงไม่เกิน `limit` แถว + "ดูทั้งหมด (N)" ไปหน้ารายการที่กรองไว้ (ถ้ามี `viewAllTo`)
 * - แต่ละแถวเป็นลิงก์ไปหน้ารายละเอียดของมัน (`rowTo`)
 */
export interface RelatedRecordsCardProps<T> {
  title: string;
  icon?: React.ReactNode;
  /** คีย์สิทธิ์ดูของชนิดปลายทาง เช่น 'sites.view' */
  permission: string;
  queryKey: readonly unknown[];
  /** ดึงข้อมูล — คืน { items, total } (list endpoint ปกติของโมดูลนั้น กรองด้วย id ของหน้านี้) */
  fetcher: () => Promise<{ items: T[]; total: number }>;
  rowKey: (row: T) => React.Key;
  /** เนื้อหาแถว (ซ้าย = ชื่อ/รหัส · ขวา = สถานะ/ยอด) */
  renderRow: (row: T) => React.ReactNode;
  /** คืนค่าว่าง/null = แถวนั้นไม่มีหน้าปลายทาง (ไม่ทำลิงก์) */
  rowTo?: (row: T) => string | null | undefined;
  /** state ที่ส่งไปกับลิงก์แถว (เช่น detailNavState เพื่อกลับมาหน้านี้) */
  rowState?: unknown;
  viewAllTo?: string;
  emptyText?: string;
  testId?: string;
}

export function RelatedRecordsCard<T>({
  title, icon, permission, queryKey, fetcher, rowKey, renderRow, rowTo, rowState, viewAllTo,
  emptyText = 'ยังไม่มีข้อมูล', testId,
}: RelatedRecordsCardProps<T>) {
  const { hasPermission } = usePermission();
  const allowed = hasPermission(permission);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: fetcher,
    enabled: allowed,
    staleTime: 30 * 1000,
  });
  if (!allowed) return null;

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  return (
    <DetailSection
      title={total > 0 ? `${title} (${total.toLocaleString()})` : title}
      icon={icon}
      padded={false}
      testId={testId}
      actions={
        viewAllTo && total > 0 ? (
          <Link to={viewAllTo} className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800">
            ดูทั้งหมด
            <RiArrowRightSLine className="h-4 w-4" />
          </Link>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-secondary-500">
          <RiLoader4Line className="h-5 w-5 animate-spin mr-2 text-primary-500" />
          กำลังโหลด...
        </div>
      ) : isError ? (
        <div className="px-4 py-4 sm:px-6 text-sm text-danger-700">
          {getApiErrorMessage(error, 'โหลดข้อมูลไม่สำเร็จ')}{' '}
          <button type="button" onClick={() => refetch()} className="underline">ลองอีกครั้ง</button>
        </div>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 sm:px-6 text-sm text-secondary-500 text-center">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-secondary-100">
          {items.map((row) => {
            const body = (
              <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 min-w-0">{renderRow(row)}</div>
            );
            const to = rowTo?.(row);
            return (
              <li key={rowKey(row)}>
                {to ? (
                  <Link to={to} state={rowState} className="block hover:bg-secondary-50 transition-colors">
                    {body}
                  </Link>
                ) : body}
              </li>
            );
          })}
          {total > items.length && (
            <li className="px-4 py-2 sm:px-6 text-xs text-secondary-500">
              แสดง {items.length.toLocaleString()} จาก {total.toLocaleString()} รายการ
            </li>
          )}
        </ul>
      )}
    </DetailSection>
  );
}

export default RelatedRecordsCard;
